import React from 'react';
import { Checkbox, Select } from 'antd';
import type { FD110 } from '../../../services/d211Api';
import { dateToInput, inputToDate, isFieldDisabled, PURCHASE_CATEGORY } from '../helpers';

interface Tab1Props {
  formData: Partial<FD110>;
  names: Record<string, string>;
  isEditing: boolean;
  setField: (key: string, value: unknown) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  formData, names, isEditing, setField, resolveSingleName,
}) => {
  const disabled = !isEditing;

  return (
    <div className="d211-tab-content">
      {/* Row 1: 採購單號 / 採購日期 */}
      <div className="form-grid">
        <label>採購單號</label>
        <input className="form-input" value={formData.fd110_01 || ''} disabled readOnly />

        <label>採購日期</label>
        <input className="form-input" type="date"
          value={dateToInput(formData.fd110_11)}
          onChange={e => setField('fd110_11', inputToDate(e.target.value))}
          disabled={isFieldDisabled('fd110_11', isEditing)} />
      </div>

      {/* Row 2: 分包商 / 幣別 / 匯率 */}
      <div className="form-grid">
        <label>分包商代號</label>
        <div className="lookup-field">
          <input className="form-input" value={formData.fd110_12 || ''}
            onChange={e => setField('fd110_12', e.target.value)}
            onBlur={() => resolveSingleName('supplier', 'supplier', formData.fd110_12 || '')}
            disabled={disabled} />
          <span className="lookup-name">{names.supplier || ''}</span>
        </div>

        <label>幣別</label>
        <div className="lookup-field">
          <input className="form-input" style={{ width: 80 }} value={formData.fd110_19 || ''}
            onChange={e => setField('fd110_19', e.target.value)}
            onBlur={() => resolveSingleName('currency', 'currency', formData.fd110_19 || '')}
            disabled={disabled} />
          <span className="lookup-name">{names.currency || ''}</span>
        </div>

        <label>匯率</label>
        <input className="form-input" type="number" step="0.0001" style={{ width: 100 }}
          value={formData.fd110_20 ?? 1}
          onChange={e => setField('fd110_20', parseFloat(e.target.value) || 0)}
          disabled={disabled} />
      </div>

      {/* Row 3: 購貨類別 / 材料類別 */}
      <div className="form-grid">
        <label>購貨類別</label>
        <Select
          value={formData.fd110_28 || undefined}
          onChange={v => setField('fd110_28', v)}
          disabled={disabled}
          style={{ width: 120 }}
          options={Object.entries(PURCHASE_CATEGORY).map(([k, v]) => ({ value: k, label: v }))}
        />

        <label>材料類別</label>
        <Select
          value={formData.fd110_46 || undefined}
          onChange={v => setField('fd110_46', v)}
          disabled={disabled}
          style={{ width: 120 }}
          options={[
            { value: '1', label: '原料' },
            { value: '2', label: '半成品' },
            { value: '3', label: '成品' },
          ]}
        />
      </div>

      {/* Row 4: 採購人員 / QC人員 */}
      <div className="form-grid">
        <label>採購人員</label>
        <div className="lookup-field">
          <input className="form-input" value={formData.fd110_31 || ''}
            onChange={e => setField('fd110_31', e.target.value)}
            onBlur={() => resolveSingleName('buyer', 'employee', formData.fd110_31 || '')}
            disabled={disabled} />
          <span className="lookup-name">{names.buyer || ''}</span>
        </div>

        <label>Q.C人員代號</label>
        <div className="lookup-field">
          <input className="form-input" value={formData.fd110_30 || ''}
            onChange={e => setField('fd110_30', e.target.value)}
            onBlur={() => resolveSingleName('qcPerson', 'employee', formData.fd110_30 || '')}
            disabled={disabled} />
          <span className="lookup-name">{names.qcPerson || ''}</span>
        </div>
      </div>

      {/* Row 5: 交易條件 / 付款條件 / 包裝 */}
      <div className="form-grid">
        <label>交易條件</label>
        <div className="lookup-field">
          <input className="form-input" style={{ width: 80 }} value={formData.fd110_141 || ''}
            onChange={e => setField('fd110_141', e.target.value)}
            onBlur={() => resolveSingleName('tradeTerms', 'tradeTerms', formData.fd110_141 || '')}
            disabled={disabled} />
          <input className="form-input" value={formData.fd110_14 || ''} disabled readOnly
            style={{ flex: 1 }} />
        </div>

        <label>付款條件</label>
        <div className="lookup-field">
          <input className="form-input" style={{ width: 80 }} value={formData.fd110_151 || ''}
            onChange={e => setField('fd110_151', e.target.value)}
            onBlur={() => resolveSingleName('paymentTerms', 'paymentTerms', formData.fd110_151 || '')}
            disabled={disabled} />
          <input className="form-input" value={formData.fd110_15 || ''} disabled readOnly
            style={{ flex: 1 }} />
        </div>

        <label>包裝</label>
        <div className="lookup-field">
          <input className="form-input" value={formData.fd110_16 || ''}
            onChange={e => setField('fd110_16', e.target.value)}
            onBlur={() => resolveSingleName('packing', 'packing', formData.fd110_16 || '')}
            disabled={disabled} />
          <span className="lookup-name">{names.packing || ''}</span>
        </div>
      </div>

      {/* Row 6: 收貨公司 / 生產通知單號 / 樣布需要量 */}
      <div className="form-grid">
        <label>收貨公司代號</label>
        <div className="lookup-field">
          <input className="form-input" value={formData.fd110_33 || ''}
            onChange={e => setField('fd110_33', e.target.value)}
            onBlur={() => resolveSingleName('receivingCo', 'supplier', formData.fd110_33 || '')}
            disabled={disabled} />
          <span className="lookup-name">{names.receivingCo || ''}</span>
        </div>

        <label>生產通知單號</label>
        <input className="form-input" value={formData.fd110_34 || ''}
          onChange={e => setField('fd110_34', e.target.value)}
          disabled={disabled} />

        <label>樣布需要量</label>
        <input className="form-input" value={formData.fd110_45 || ''}
          onChange={e => setField('fd110_45', e.target.value)}
          disabled={disabled} />
      </div>

      {/* Row 7: 三聯發票 */}
      <div className="form-grid">
        <label>三聯發票</label>
        <Checkbox
          checked={!!formData.fd110_55}
          onChange={e => setField('fd110_55', e.target.checked)}
          disabled={disabled}
        >開立三聯式發票</Checkbox>
      </div>

      {/* Row 8: 包裝說明(唯讀) / 輸單人員 / 金額彙總 */}
      <div className="form-grid">
        <label>包裝說明</label>
        <textarea className="form-textarea" value={formData.fd110_161 || ''} readOnly disabled rows={2} />

        <label>輸單人員</label>
        <div className="lookup-field">
          <input className="form-input" value={formData.fd110_91 || ''} disabled readOnly />
          <span className="lookup-name">{names.entryPerson || ''}</span>
        </div>
      </div>

      {/* Row 9: 金額 */}
      <div className="form-grid">
        <label>採購單總金額</label>
        <input className="form-input" value={formData.fd110_23 ?? 0} disabled readOnly />

        <label>人民幣總金額</label>
        <input className="form-input" value={formData.fd110_24 ?? 0} disabled readOnly />
      </div>
    </div>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
